import { useEffect, useContext } from 'react';

import {
    Routes,
    Route,
    createBrowserRouter,
    RouterProvider,
    useNavigate,
    useLocation,
    useBlocker,
} from 'react-router-dom';

import { pendoService } from '@utils/pendo';

import AuthLayout from '@layouts/Auth/Auth.layout';
import ChatLayout from '@layouts/Chat/Chat.layout';
import UtilLayout from '@layouts/Util/Util.layout';

import SSOLogin from '@pages/SSOLogin/SSOLogin.page';
//import FigmaLogin from '@pages/FigmaLogin/FigmaLogin.page';
import Dashboard from '@pages/Dashboard/Dashboard.page';
import ManageAdmins from '@pages/ManageAdmins/ManageAdmins.page';
import Analytics from '@pages/Analytics/Analytics.page';
import Session from '@pages/Session/Session.page';
import PublicProject from '@pages/PublicProject';
// import BadRoute from '@pages/BadRoute/BadRoute.page';
import '@appkit4/react-components/dist/styles/appkit4-react.min.css';
import styles from './App.module.scss';
import { UserContext } from '@contexts/UserProvider';
import { isEmpty } from 'lodash';
import BadRoute from '@pages/BadRoute/BadRoute.page';

// List of routes that don't require authentication
const PUBLIC_ROUTES = ['/login', '/public/project'];

const Root = () => {
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const location = useLocation();

    useEffect(() => {
        // Check if current path is a public route (no auth required)
        const isPublicRoute = PUBLIC_ROUTES.some((route) => location.pathname.startsWith(route));

        if (isEmpty(user) && !isPublicRoute) {
            navigate('/login', { replace: true });
            return;
        }
        if (!isEmpty(user) && location.pathname === '/') {
            navigate('/dashboard', { replace: true });
            return;
        }
    }, [user, location.pathname, navigate]);

    // Identify user with Pendo when user data is available
    useEffect(() => {
        if (!isEmpty(user)) {
            const visitorData = {
                id: user.email || user.sub || 'anonymous',
                email: user.email,
                full_name:
                    user.given_name && user.family_name
                        ? `${user.given_name} ${user.family_name}`
                        : user.name || user.given_name || 'Unknown User',
                given_name: user.given_name,
                family_name: user.family_name,
                role: user.role || 'user',
            };

            // Use email domain as account ID if available
            const accountData = user.email
                ? {
                      id: user.email.split('@')[1] || 'default-account',
                      name: user.email.split('@')[1] || 'Default Account',
                  }
                : undefined;

            pendoService.identify(visitorData, accountData);
        }
    }, [user]);

    return (
        <Routes>
            {/* Public routes - no authentication required */}
            <Route path="/public/project" element={<PublicProject />} />

            {/* Auth routes */}
            <Route element={<AuthLayout />}>
                <Route path="/login" element={<SSOLogin />} />
                {/* <Route path="/figma" element={<FigmaLogin />} /> */}
            </Route>

            {/* Protected routes */}
            <Route element={<ChatLayout />}>
                <Route path="/session" element={<BlockedSession />} />
            </Route>
            <Route element={<UtilLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
            </Route>
            <Route element={<UtilLayout />}>
                <Route path="/admins" element={<ManageAdmins />} />
            </Route>
            <Route element={<UtilLayout />}>
                <Route path="/analytics" element={<Analytics />} />
            </Route>

            {/* Catch-all route - must be last */}
            <Route path="*" element={<BadRoute />} />
        </Routes>
    );
};

function BlockedSession() {
    const blocker = useBlocker(true);
    useEffect(() => {
        if (blocker.state === 'blocked') {
            if (
                window.confirm(
                    'Are you sure you want to leave the session page? Please make sure you have stopped the generation streaming, otherwise, the project will lost all the changes.'
                )
            ) {
                blocker.proceed();
            } else {
                blocker.reset();
            }
        }
    }, [blocker]);
    return <Session />;
}

const router = createBrowserRouter([{ path: '*', element: <Root /> }], {
    basename: '/',
});

const App = () => {
    useEffect(() => {
        // Initialize Pendo when the app loads
        pendoService.initialize();
    }, []);

    return (
        <div className={styles.App}>
            <RouterProvider router={router} />
        </div>
    );
};

export default App;
